(() => {
  'use strict';

  const logic = window.EdiBotProductLogic;
  if (!logic || document.documentElement.dataset.edpInitialized === 'true') return;
  document.documentElement.dataset.edpInitialized = 'true';

  let config;
  let state;
  let stickyObserver;
  let stickyFallbackInstalled = false;
  let stickyFallbackHandler;
  let isSubmitting = false;
  let purchaseNeedsCartReview = false;
  let viewportVideoObserver;
  let viewportVideoEntries = new Map();

  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const galleryRuntime = new WeakMap();
  const mediaWarmPromises = new WeakMap();
  const MEDIA_EAGER_WINDOW = 3;
  const MEDIA_READY_TIMEOUT = 4000;

  function initializeState(force = false) {
    const configNode = document.querySelector('[data-edp-config]');
    if (!configNode) return false;
    if (state && !force) return true;

    try {
      config = JSON.parse(configNode.textContent);
      state = logic.createState(config);
    } catch (error) {
      console.error('Could not initialize the EdiBot product page.', error);
      return false;
    }

    return true;
  }

  function ui(message) {
    if (typeof message !== 'string' || !config?.labels) return message;
    if (Object.prototype.hasOwnProperty.call(config.labels, message)) return config.labels[message];
    for (const [pattern, translated] of Object.entries(config.labels)) {
      const tokens = pattern.match(/%[a-z_]+%/g);
      if (!tokens) continue;
      const expression = pattern.split(/%[a-z_]+%/g).map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('(.+?)');
      const match = message.match(new RegExp('^' + expression + '$'));
      if (match) return tokens.reduce((result, token, index) => result.replace(token, match[index + 1]), translated);
    }
    return message;
  }

  function updateCtas(previewMode, unavailable) {
    all('[data-edp-add]').forEach((button) => {
      button.disabled = previewMode || (!purchaseNeedsCartReview && unavailable) || isSubmitting;
      button.setAttribute('aria-disabled', String(button.disabled));
      if (!isSubmitting) button.textContent = ui(purchaseNeedsCartReview ? 'View cart' : unavailable ? 'Sold out' : 'Add to cart');
    });

    all('[data-edp-buy]').forEach((button) => {
      button.disabled = previewMode || (!purchaseNeedsCartReview && unavailable) || isSubmitting;
      button.setAttribute('aria-disabled', String(button.disabled));
      if (!isSubmitting) button.textContent = ui(purchaseNeedsCartReview ? 'View cart' : unavailable ? 'Sold out' : 'Buy now');
    });

    all('[data-edp-preview-note]').forEach((node) => {
      node.hidden = !previewMode;
    });

    const showEmptyMedia = previewMode || Boolean(window.Shopify && window.Shopify.designMode);
    all('.edp-media-slot.is-placeholder').forEach((slot) => {
      slot.hidden = !showEmptyMedia;
    });
  }

  function render() {
    if (!state) return;

    const set = state.config.sets[state.selectedSet];
    const launchGiftIncluded = logic.hasLaunchGift(state);
    const careIncluded = set.inkCareIncluded || launchGiftIncluded;
    const price = logic.getDisplayPrice(state);
    const variant = logic.resolveVariant(state);

    all('[data-edp-set]').forEach((button) => {
      const active = button.dataset.edpSet === state.selectedSet;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    all('[data-edp-color]').forEach((button) => {
      const active = button.dataset.edpColor === state.selectedColor;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    all('[data-edp-selected-set]').forEach((node) => {
      node.textContent = ui(set.name);
    });
    all('[data-edp-selected-color]').forEach((node) => {
      node.textContent = ui(state.selectedColor);
    });
    all('[data-edp-price]').forEach((node) => {
      node.textContent = ui(logic.formatMoney(price.total, config.money));
    });
    all('[data-edp-compare-price]').forEach((node) => {
      node.hidden = !price.compareAt;
      node.textContent = ui(price.compareAt ? logic.formatMoney(price.compareAt, config.money) : '');
    });
    all('[data-edp-set-price]').forEach((node) => {
      const setPrice = logic.getSetPrice(state, node.dataset.edpSetPrice);
      node.textContent = ui(setPrice ? logic.formatMoney(setPrice.price, config.money) : '');
    });
    all('[data-edp-set-compare-price]').forEach((node) => {
      const setPrice = logic.getSetPrice(state, node.dataset.edpSetComparePrice);
      node.hidden = !setPrice || !setPrice.compareAt;
      node.textContent = ui(setPrice && setPrice.compareAt
        ? logic.formatMoney(setPrice.compareAt, config.money)
        : '');
    });
    all('[data-edp-ink-price]').forEach((node) => {
      node.textContent = ui(logic.formatMoney(config.inkCare.price, config.money));
    });

    all('[data-edp-ink-panel]').forEach((node) => {
      node.hidden = careIncluded;
    });
    all('[data-edp-ink-included]').forEach((node) => {
      node.hidden = !careIncluded;
    });
    all('[data-edp-ink-care]').forEach((input) => {
      input.checked = state.inkCareSelected;
      input.disabled = !config.inkCare.variantId || !config.inkCare.available;
      input.setAttribute(
        'aria-label',
        ui(`Add 1-Year Ink Care for ${logic.formatMoney(config.inkCare.price, config.money)}`));
    });
    all('[data-edp-creator-rewards]').forEach((input) => {
      input.checked = state.creatorRewardsSelected;
    });
    const rulerAvailable = logic.canSelectLongRuler(state);
    all('[data-edp-long-ruler]').forEach((input) => {
      input.checked = launchGiftIncluded || state.longRulerSelected;
      input.disabled = launchGiftIncluded || !rulerAvailable;
      input.hidden = launchGiftIncluded;
      input.closest('.edp-program-option')?.classList.toggle('is-included', launchGiftIncluded);
      input.setAttribute('aria-label', ui(launchGiftIncluded ? 'Large-format Positioning Ruler included as a launch gift' : rulerAvailable
        ? `Add Large-format Positioning Ruler for ${logic.formatMoney(config.longRuler.price, config.money)}`
        : 'Large-format Positioning Ruler is sold out'));
    });
    all('[data-edp-ruler-price]').forEach((node) => {
      node.textContent = ui(logic.formatMoney(config.longRuler ? config.longRuler.price : 0, config.money));
    });
    all('[data-edp-ruler-cost]').forEach((node) => { node.hidden = launchGiftIncluded || !rulerAvailable; });
    all('[data-edp-ruler-unavailable]').forEach((node) => { node.hidden = launchGiftIncluded || rulerAvailable; });
    all('[data-edp-ruler-check]').forEach(node => { node.hidden = !launchGiftIncluded; });
    all('[data-edp-ruler-included]').forEach(node => { node.hidden = !launchGiftIncluded; });
    renderLaunchGift();
    all('[data-edp-ink-status]').forEach((node) => {
      node.textContent = ui(careIncluded
        ? 'Included with your selected set'
        : state.inkCareSelected
          ? 'Added to your selected set'
          : `Optional add-on · +${logic.formatMoney(config.inkCare.price, config.money)}`);
    });

    all('[data-edp-box-list]').forEach((list) => {
      const items = logic.getBoxContents(state).map((item) => {
        const listItem = document.createElement('li');
        listItem.textContent = ui(item);
        return listItem;
      });
      list.replaceChildren(...items);
    });
    all('[data-edp-compare-set]').forEach((node) => {
      node.classList.toggle('is-selected', node.dataset.edpCompareSet === state.selectedSet);
    });
    const variantFlatlays = all('[data-edp-variant-flatlay]');
    const hasVariantFlatlay = Boolean(variant && variantFlatlays.some(
      (node) => node.dataset.edpVariantFlatlay === String(variant.id)
    ));
    variantFlatlays.forEach((node) => {
      const active = Boolean(variant && node.dataset.edpVariantFlatlay === String(variant.id));
      node.hidden = !active;
      node.classList.toggle('is-active', active);
    });
    all('[data-edp-flatlay]').forEach((node) => {
      const active = !hasVariantFlatlay && node.dataset.edpFlatlay === state.selectedSet;
      node.hidden = !active;
      node.classList.toggle('is-active', active);
    });
    all('[data-edp-final-status]').forEach((node) => {
      if (launchGiftIncluded) {
        node.textContent = ui('1-Year Ink Care + Large-format Positioning Ruler included · First 100 sets');
      } else if (set.inkCareIncluded) {
        const launchPrefix = state.selectedSet === 'family' && state.config.launchOfferActive
          ? 'Launch offer · '
          : '';
        node.textContent = ui(`${launchPrefix}Ink Care included`);
      } else {
        node.textContent = ui(state.inkCareSelected
          ? `Ink Care added · ${logic.formatMoney(config.inkCare.price, config.money)}`
          : `Ink Care optional · ${logic.formatMoney(config.inkCare.price, config.money)}`);
      }
      if (state.longRulerSelected && !launchGiftIncluded) node.textContent += ui(' · Large-format Positioning Ruler added');
    });

    const unavailable = !config.previewMode && (!variant || !variant.available);
    updateCtas(Boolean(config.previewMode), unavailable);

    document.dispatchEvent(
      new CustomEvent('edibot:change', {
        detail: { state, set, price, variant },
      })
    );
  }

  function renderLaunchGift() {
    const gift = state.config.launchGift;
    const includedGifts = gift?.fulfillmentMode === 'included' || config.launchGift?.fulfillmentMode === 'included';
    const stale = includedGifts && gift?.stale;
    const known = gift && gift.enabled && Number.isInteger(gift.remaining);
    const remaining = known ? Math.max(0, Math.min(gift.total, gift.remaining)) : null;
    const available = known && gift.available && remaining > 0;
    all('[data-edp-launch]').forEach(node => { node.hidden = !gift && !config.launchGift; node.classList.toggle('is-ended', known && !available); });
    all('[data-edp-gift-value]').forEach(node => { node.textContent = ui(logic.formatMoney(config.inkCare.price + (config.longRuler?.price || 0), config.money)); });
    all('[data-edp-gift-title]').forEach(node => { node.textContent = ui(!known ? 'Launch gifts' : !available ? 'Launch gifts fully claimed' : 'Free Ink Care + Large-format Positioning Ruler'); });
    all('[data-edp-gift-note]').forEach(node => { node.textContent = ui(!known ? 'Availability could not be confirmed. Refresh to check.' : !available ? 'Ink Care and ruler remain available as paid extras' : state.selectedSet === 'first' ? 'Choose Family or Ready-to-Serve to qualify' : 'Included with your selected set'); });
    all('[data-edp-gift-remaining]').forEach(node => { node.textContent = ui(known ? `${stale ? 'Last known: ' : ''}${remaining} / ${gift.total} left` : 'Checking availability…'); });
    all('[data-edp-gift-progress]').forEach(node => { node.hidden = !known; if (known) { node.setAttribute('aria-valuenow', remaining); node.setAttribute('aria-valuetext', ui(`${stale ? 'Last known: ' : ''}${remaining} of ${gift.total} gift packs remaining`)); } });
    all('[data-edp-gift-fill]').forEach(node => { node.style.width = known ? `${remaining / gift.total * 100}%` : '0%'; });
    all('[data-edp-gift-terms]').forEach(node => {
      node.textContent = ui(stale
        ? 'Last known stock shown · Stock update temporarily unavailable'
        : known && !available
          ? 'Thank you for celebrating our launch.'
          : includedGifts
            ? '1 gift pack per eligible set · Listed with your set in your order'
            : '1 pack per eligible set · Availability confirmed at checkout');
    });
    all('[data-edp-compare-care], [data-edp-compare-ruler]').forEach(node => {
      const key = node.dataset.edpCompareCare || node.dataset.edpCompareRuler;
      node.textContent = ui(logic.hasLaunchGift(state, key) ? 'Launch gift included' : 'Optional add-on');
    });
    all('[data-edp-compare-launch-badge]').forEach(node => {
      node.hidden = !state.config.launchOfferActive;
    });
    all('.edp-set-card[data-edp-set], [data-edp-compare-set]').forEach(node => {
      const key = node.dataset.edpSet || node.dataset.edpCompareSet;
      const copy = node.querySelector('.edp-set-card__contents, [data-edp-set-description]');
      if (!copy) return;
      const hasGift = logic.hasLaunchGift(state, key);
      let description = '';
      if (key === 'family') {
        description = hasGift
          ? 'Launch offer: Free 1-Year Ink Care + Large-format Positioning Ruler'
          : 'Ink Care optional';
      } else if (key === 'ready') {
        description = hasGift
          ? 'Family Set launch benefits + 1 extra Ink Pod'
          : '1 extra Ink Pod · Ink Care optional';
      }
      copy.textContent = ui(description);
      copy.hidden = !description;
    });
  }

  async function addCartItems(items, sections) {
    const controller = new AbortController();
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        reject(new Error('Adding your set is taking too long.'));
        controller.abort();
      }, 12000);
    });
    try {
      return await Promise.race([timeout, (async () => {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, ...(sections?.length ? { sections } : {}) }),
        });
        const body = await response.json();
        if (!response.ok) throw new Error(body.description || body.message || 'Could not add this set.');
        return body;
      })()]);
    } finally { clearTimeout(timer); }
  }

  function purchaseSections() {
    const detail = { sections: [] };
    document.dispatchEvent(new CustomEvent('cart:bundled-sections', { detail }));
    // Shopify accepts at most five section IDs per bundled render request.
    return [...new Set(detail.sections.filter(id => typeof id === 'string' && id.trim()))].slice(0, 5);
  }

  async function addSelectionToCart(currentState, sections) {
    if (currentState.config.previewMode) throw new Error('Preview mode cannot add to cart.');

    const includedGifts = currentState.config.launchGift?.fulfillmentMode === 'included' || config.launchGift?.fulfillmentMode === 'included';
    const giftApi = window.EdiBotLaunchGift;
    const legacyGiftPresent = Boolean(currentState.config.launchGift?.legacyGiftPresent || config.launchGift?.legacyGiftPresent);
    const canMigrateLegacy = typeof giftApi?.needsLegacyMigration === 'function' && typeof giftApi?.sync === 'function';
    const legacyMigrationPending = includedGifts && (typeof giftApi?.needsLegacyMigration === 'function'
      ? giftApi.needsLegacyMigration()
      : legacyGiftPresent);
    if (includedGifts && (legacyGiftPresent || legacyMigrationPending) && !canMigrateLegacy) {
      purchaseNeedsCartReview = true;
      throw new Error('Your saved cart contains a previous gift pack. Refresh this page or select View cart to review it before continuing.');
    }
    const giftEligible = Boolean(config.launchGift?.variantId && config.launchGift.eligibleSets?.includes(currentState.selectedSet));
    if (giftEligible && !includedGifts) {
      if (!window.EdiBotLaunchGift) throw new Error('Could not verify launch gifts. Please refresh and try again.');
      setPurchaseStatus('Checking your free gifts…');
      const wasIncluded = logic.hasLaunchGift(currentState);
      const fresh = await window.EdiBotLaunchGift.refresh();
      currentState = { ...currentState, config: { ...currentState.config, launchGift: fresh } };
      if (wasIncluded && !logic.hasLaunchGift(currentState)) throw new Error('Launch gifts have just sold out. Please review your selection before adding it again.');
    }
    const items = logic.buildCartItems(currentState);

    setPurchaseStatus('Adding your selected set…');
    // A timed-out POST may still have reached Shopify. Never repeat it automatically.
    purchaseNeedsCartReview = true;
    let added = false;
    try {
      let body = await addCartItems(items, sections);
      added = true;
      // New included-gift purchases need only the add response. Existing saved
      // carts may still carry a chargeable legacy gift SKU and need migration.
      if (giftApi && (!includedGifts || (canMigrateLegacy && giftApi.needsLegacyMigration()))) {
        setPurchaseStatus(includedGifts ? 'Updating your saved cart…' : 'Checking your cart and applying eligible gifts…');
        body = await giftApi.sync(body, { sections, parallelStatus: giftEligible, deferSectionFallback: true });
      }
      purchaseNeedsCartReview = false;
      return body;
    } catch (error) {
      throw new Error(added
        ? `Your set was added. ${error.message} Select View cart to review your gifts and continue.`
        : `${error.message} Your set may already be in your cart. Select View cart to check before adding it again.`);
    }
  }

  function setPurchaseStatus(message, isError = false) {
    all('[data-edp-purchase-status]').forEach((node) => {
      node.textContent = ui(message);
      node.classList.toggle('is-error', isError);
    });
  }

  function setSubmitting(sourceButton, submitting) {
    isSubmitting = submitting;
    all('[data-edp-add], [data-edp-buy]').forEach((button) => {
      button.setAttribute('aria-busy', String(submitting));
      if (submitting) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }
    });
    if (submitting && sourceButton) {
      sourceButton.textContent = ui('Adding…');
      sourceButton.disabled = true;
      sourceButton.setAttribute('aria-disabled', 'true');
    } else {
      render();
    }
  }

  async function submitSelection(sourceButton, buyNow) {
    if (!state || isSubmitting) return;
    if (purchaseNeedsCartReview) {
      window.location.assign((window.Shopify?.routes?.root || '/') + 'cart');
      return;
    }

    setSubmitting(sourceButton, true);
    setPurchaseStatus('');
    try {
      const cart = await addSelectionToCart(state, buyNow ? undefined : purchaseSections());
      if (buyNow) {
        setPurchaseStatus('Opening secure checkout…');
        window.location.assign((window.Shopify?.routes?.root || '/') + 'checkout');
        return;
      }
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true, sections: cart?.sections } })
      );
      setPurchaseStatus('Your selected set was added to the cart.');
    } catch (error) {
      setPurchaseStatus(error.message || 'Could not add this set.', true);
    } finally {
      setSubmitting(sourceButton, false);
    }
  }

  function startBestEffortDecode(image) {
    if (!image || typeof image.decode !== 'function') return;
    try {
      const decoding = image.decode();
      if (decoding && typeof decoding.catch === 'function') {
        decoding.catch(() => {});
      }
    } catch (_error) {
      // Decode is only an enhancement; readiness must not depend on it.
    }
  }

  function getGalleryRuntime(group) {
    let runtime = galleryRuntime.get(group);
    if (!runtime) {
      runtime = { requestId: 0, warmQueueStarted: false };
      galleryRuntime.set(group, runtime);
    }
    return runtime;
  }

  function getMediaPanelForTab(tab) {
    const group = tab.closest('[data-edp-media-group]');
    if (!group) return null;
    const target = tab.dataset.edpMediaTab || tab.getAttribute('aria-controls');
    if (!target) return null;
    return all('[data-edp-media-panel]', group).find(
      (panel) => panel.dataset.edpMediaPanel === target || panel.id === target
    ) || null;
  }

  function warmMediaForTab(tab, priority = 'low') {
    const panel = getMediaPanelForTab(tab);
    if (!panel) return Promise.resolve();

    const image = panel.querySelector('img');
    if (!image) return Promise.resolve();

    image.loading = 'eager';
    image.fetchPriority = priority;

    const existing = mediaWarmPromises.get(image);
    if (existing) return existing;

    const promise = new Promise((resolve) => {
      let settled = false;
      let timeoutId;

      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        resolve();
        if (image.complete && image.naturalWidth > 0) {
          startBestEffortDecode(image);
        }
      };

      timeoutId = window.setTimeout(finish, MEDIA_READY_TIMEOUT);
      if (image.complete) {
        finish();
      } else {
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
      }
    });

    mediaWarmPromises.set(image, promise);
    return promise;
  }

  function commitMediaTab(tab, moveFocus = false, scrollIntoView = true) {
    const group = tab.closest('[data-edp-media-group]');
    if (!group) return;
    const target = tab.dataset.edpMediaTab || tab.getAttribute('aria-controls');
    if (!target) return;

    all('[data-edp-media-tab]', group).forEach((button) => {
      const active = button === tab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
      button.setAttribute('tabindex', active ? '0' : '-1');
    });
    all('[data-edp-media-panel]', group).forEach((panel) => {
      const active = panel.dataset.edpMediaPanel === target || panel.id === target;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
      if (!active) {
        if (window.EdiBotVideo) all('video', panel).forEach((video) => window.EdiBotVideo.pause(video));
        else all('video', panel).forEach((video) => video.pause());
      }
    });
    all('[data-edp-video-trigger]', group).forEach((button) => {
      button.hidden = button.dataset.edpVideoCover !== target;
    });

    if (scrollIntoView) tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (moveFocus) tab.focus();
  }

  function playGalleryVideo(tab, moveFocus = false) {
    const group = tab.closest('[data-edp-media-group]');
    const panel = getMediaPanelForTab(tab);
    const video = panel?.querySelector('video');
    if (!group || !video) return false;

    getGalleryRuntime(group).requestId += 1;
    commitMediaTab(tab, moveFocus);
    video.controls = true;
    video.playsInline = true;
    if (window.EdiBotVideo) return window.EdiBotVideo.play(video, { manual: true });
    const onFailure = (error) => {
      if (error.name !== 'AbortError') console.warn('EdiBot gallery playback failed:', error.name);
    };
    try {
      Promise.resolve(video.play()).catch(onFailure);
    } catch (error) {
      onFailure(error);
    }
    return true;
  }

  async function requestMediaActivation(tab, moveFocus = false, scrollIntoView = true) {
    const group = tab.closest('[data-edp-media-group]');
    if (!group) return;
    const runtime = getGalleryRuntime(group);
    runtime.requestId += 1;
    const requestId = runtime.requestId;

    const panel = getMediaPanelForTab(tab);
    const image = panel ? panel.querySelector('img') : null;
    if (image && !(image.complete && image.naturalWidth > 0)) {
      await warmMediaForTab(tab, 'high');
    }

    if (requestId !== runtime.requestId) return;
    if (!tab.isConnected || !group.isConnected) return;
    commitMediaTab(tab, moveFocus, scrollIntoView);
  }

  function syncVariantMedia() {
    const variant = state && logic.resolveVariant(state);
    all('[data-edp-hero] [data-edp-media-group]').forEach((group) => {
      getGalleryRuntime(group).requestId += 1;
      const tabs = all('[data-edp-media-tab]', group);
      const selectedTab = variant && variant.mediaId
        ? tabs.find((tab) => tab.dataset.edpMediaId === String(variant.mediaId))
        : null;
      const targetTab = selectedTab || tabs[0];
      if (targetTab) requestMediaActivation(targetTab, false, false);
    });
  }

  function scheduleMediaWarmQueue(group, activeIndex) {
    const runtime = getGalleryRuntime(group);
    if (runtime.warmQueueStarted) return;
    runtime.warmQueueStarted = true;

    const tabs = all('[data-edp-media-tab]', group);
    const order = logic.getMediaPreloadOrder(activeIndex, tabs.length);
    order.slice(0, MEDIA_EAGER_WINDOW).forEach((index) => {
      const tab = tabs[index];
      if (tab) warmMediaForTab(tab, 'low');
    });

    const idleOrder = order.slice(MEDIA_EAGER_WINDOW);
    const warmNext = () => {
      if (!group.isConnected) return;
      const index = idleOrder.shift();
      if (!Number.isInteger(index)) return;
      const tab = tabs[index];
      const continueQueue = () => scheduleNext();
      if (!tab || !tab.isConnected) {
        continueQueue();
        return;
      }
      warmMediaForTab(tab, 'low').then(continueQueue);
    };
    const scheduleNext = () => {
      if (!idleOrder.length || !group.isConnected) return;
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warmNext, { timeout: 1800 });
      } else {
        window.setTimeout(warmNext, 250);
      }
    };
    scheduleNext();
  }

  function initializeMediaGroups(root = document) {
    window.EdiBotVideo?.init(root);
    const groups = [];
    if (root.matches && root.matches('[data-edp-media-group]')) groups.push(root);
    all('[data-edp-media-group]', root).forEach((group) => groups.push(group));
    groups.forEach((group) => {
      const firstTab = group.querySelector('[data-edp-media-tab]');
      if (!firstTab) return;
      commitMediaTab(firstTab);
      scheduleMediaWarmQueue(group, 0);
      window.EdiBotVideo?.warmHero(group);
    });
  }

  function pausePanelVideos(panel) {
    panel.querySelectorAll('video').forEach((video) => {
      if (typeof window !== 'undefined' && window.EdiBotVideo) window.EdiBotVideo.pause(video);
      else video.pause();
    });
  }

  function activateContentTab(tab, moveFocus = false) {
    const group = tab.closest('[data-edp-tabs]');
    if (!group) return;

    const value = tab.dataset.edpTab;
    all('[data-edp-tab]', group).forEach((button) => {
      const active = button.dataset.edpTab === value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
      button.setAttribute('tabindex', active ? '0' : '-1');
    });
    all('[data-edp-tab-panel]', group).forEach((panel) => {
      const active = panel.dataset.edpTabPanel === value;
      if (!active) pausePanelVideos(panel);
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });

    if (moveFocus) tab.focus();
    window.requestAnimationFrame(refreshViewportVideoPlayback);
  }

  function pauseManagedViewportVideos(root = document) {
    root.querySelectorAll('[data-edp-viewport-video] video').forEach((video) => {
      if (typeof window !== 'undefined' && window.EdiBotVideo) window.EdiBotVideo.pause(video);
      else video.pause();
    });
  }

  function refreshViewportVideoPlayback() {
    if (document.hidden) {
      pauseManagedViewportVideos();
      return;
    }
    const candidates = [];
    all('[data-edp-viewport-video]').forEach((wrapper) => {
      const video = wrapper.querySelector('video');
      if (!video) return;
      const anchor = wrapper.closest('[data-edp-tabs]') || wrapper;
      const near = viewportVideoEntries.get(anchor)?.isIntersecting && !anchor.closest('[hidden]');
      const hidden = Boolean(wrapper.closest('[hidden]'));
      if ((!near || hidden) && !video.paused) {
        if (window.EdiBotVideo) window.EdiBotVideo.pause(video);
        else video.pause();
      }
      if (near) candidates.push({ wrapper, video, anchor, hidden });
    });
    candidates.sort((a, b) => Number(a.hidden) - Number(b.hidden));
    candidates.forEach(({ wrapper, video, anchor }) => {
      window.EdiBotVideo?.warm(video, { anchor });
      if (video.paused) wrapper.dataset.edpPlayback = 'manual';
    });
  }

  function initializeViewportVideos() {
    window.EdiBotVideo?.init();
    if (viewportVideoObserver) viewportVideoObserver.disconnect();
    viewportVideoEntries = new Map();
    const anchors = new Set();
    all('[data-edp-viewport-video]').forEach((wrapper) => {
      const video = wrapper.querySelector('video');
      if (!video) return;
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = false;
      anchors.add(wrapper.closest('[data-edp-tabs]') || wrapper);
    });
    if (!anchors.size || !('IntersectionObserver' in window)) return;
    viewportVideoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => viewportVideoEntries.set(entry.target, { isIntersecting: entry.isIntersecting }));
      refreshViewportVideoPlayback();
    }, { rootMargin: '600px 0px', threshold: 0 });
    anchors.forEach((anchor) => viewportVideoObserver.observe(anchor));
  }

  function toggleControlledPanel(button) {
    const targetId = button.getAttribute('aria-controls') || button.dataset.edpTarget;
    const panel = targetId ? document.getElementById(targetId) : button.nextElementSibling;
    if (!panel) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  }

  function scrollToSelector() {
    const selector = document.querySelector('[data-edp-selector]');
    if (!selector) return;
    selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusTarget = selector.querySelector('[data-edp-set].is-active, [data-edp-set]');
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  function updateStickyVisibility(visible) {
    all('[data-edp-sticky], .edp-sticky').forEach((sticky) => {
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
    });
  }

  function installStickyVisibility() {
    const selector = document.querySelector('[data-edp-selector]');
    if (!selector) return;

    if (stickyObserver) stickyObserver.disconnect();
    if ('IntersectionObserver' in window) {
      stickyObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const passed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 120;
          updateStickyVisibility(passed);
        },
        { rootMargin: '-120px 0px 0px 0px', threshold: 0 }
      );
      stickyObserver.observe(selector);
      return;
    }

    stickyFallbackHandler = () => {
      const currentSelector = document.querySelector('[data-edp-selector]');
      updateStickyVisibility(Boolean(currentSelector && currentSelector.getBoundingClientRect().bottom <= 120));
    };
    if (!stickyFallbackInstalled) {
      window.addEventListener('scroll', () => stickyFallbackHandler(), { passive: true });
      window.addEventListener('resize', () => stickyFallbackHandler());
      stickyFallbackInstalled = true;
    }
    stickyFallbackHandler();
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const setButton = target.closest('[data-edp-set]');
    const colorButton = target.closest('[data-edp-color]');
    const mediaTab = target.closest('[data-edp-media-tab]');
    const videoTrigger = target.closest('[data-edp-video-trigger]');
    const contentTab = target.closest('[data-edp-tab]');
    const disclosure = target.closest('[data-edp-disclosure], [data-edp-accordion]');
    const productDetailsSummary = target.closest('[data-edp-product-details] summary');
    const scrollButton = target.closest('[data-edp-scroll-to-selector]');
    const addButton = target.closest('[data-edp-add]');
    const buyButton = target.closest('[data-edp-buy]');

    if (setButton && state) {
      state = logic.selectSet(state, setButton.dataset.edpSet);
      render();
      syncVariantMedia();
    }
    if (colorButton && state) {
      state = logic.selectColor(state, colorButton.dataset.edpColor);
      render();
      syncVariantMedia();
    }
    if (videoTrigger) {
      const group = videoTrigger.closest('[data-edp-media-group]');
      const videoPanelId = videoTrigger.getAttribute('aria-controls');
      const videoTab = group && all('[data-edp-media-tab]', group).find((tab) => tab.getAttribute('aria-controls') === videoPanelId);
      if (videoTab) playGalleryVideo(videoTab, true);
    }
    if (mediaTab && !playGalleryVideo(mediaTab)) requestMediaActivation(mediaTab);
    if (contentTab) activateContentTab(contentTab);
    if (disclosure && disclosure.tagName !== 'SUMMARY') toggleControlledPanel(disclosure);
    if (productDetailsSummary) {
      event.preventDefault();
      const productDetails = productDetailsSummary.closest('[data-edp-product-details]');
      if (productDetails) {
        const shouldOpen = !productDetails.open;
        requestAnimationFrame(() => {
          productDetails.open = shouldOpen;
        });
      }
    }
    if (scrollButton) scrollToSelector();
    if (addButton) submitSelection(addButton, false);
    if (buyButton) submitSelection(buyButton, true);
  });

  document.addEventListener('keydown', (event) => {
    const mediaTab = event.target instanceof Element ? event.target.closest('[data-edp-media-tab]') : null;
    const mediaKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (mediaTab && mediaKeys.includes(event.key)) {
      const mediaGroup = mediaTab.closest('[data-edp-media-group]');
      const mediaTabs = mediaGroup ? all('[data-edp-media-tab]', mediaGroup) : [];
      const currentIndex = mediaTabs.indexOf(mediaTab);
      const nextIndex = logic.getMediaNavigationIndex(currentIndex, mediaTabs.length, event.key);
      const nextTab = mediaTabs[nextIndex];
      if (nextTab) {
        event.preventDefault();
        requestMediaActivation(nextTab, true);
      }
      return;
    }

    const tab = event.target instanceof Element ? event.target.closest('[data-edp-tab]') : null;
    const contentTabKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!tab || !contentTabKeys.includes(event.key)) return;

    const group = tab.closest('[data-edp-tabs]');
    const tabs = group ? all('[data-edp-tab]', group) : [];
    if (!tabs.length) return;

    event.preventDefault();
    const currentIndex = tabs.indexOf(tab);
    const nextIndex = logic.getMediaNavigationIndex(currentIndex, tabs.length, event.key);
    if (tabs[nextIndex]) activateContentTab(tabs[nextIndex], true);
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof Element) || !state) return;
    if (input.matches('[data-edp-ink-care]')) {
      state = logic.setInkCare(state, input.checked);
      render();
    }
    if (input.matches('[data-edp-creator-rewards]')) {
      state = logic.setCreatorRewards(state, input.checked);
      render();
    }
    if (input.matches('[data-edp-long-ruler]')) {
      state = logic.setLongRuler(state, input.checked);
      render();
    }
  });

  function warmMediaFromIntent(event) {
    const target = event.target instanceof Element ? event.target : null;
    const mediaTab = target ? target.closest('[data-edp-media-tab]') : null;
    if (mediaTab) warmMediaForTab(mediaTab, 'high');
  }

  ['pointerenter', 'pointerdown', 'focusin'].forEach((eventName) => {
    document.addEventListener(eventName, warmMediaFromIntent, true);
  });

  document.addEventListener('visibilitychange', refreshViewportVideoPlayback);

  document.addEventListener('shopify:section:load', (event) => {
    const reloadedConfig = event.target && event.target.querySelector
      ? event.target.querySelector('[data-edp-config]')
      : null;
    if (reloadedConfig) initializeState(true);
    initializeMediaGroups(event.target);
    render();
    initializeViewportVideos();
    installStickyVisibility();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    window.EdiBotVideo?.destroy(event.target);
    pauseManagedViewportVideos(event.target);
    window.requestAnimationFrame(initializeViewportVideos);
  });

  document.addEventListener('edibot:gift-status', event => {
    if (!state || state.config.previewMode) return;
    state = { ...state, config: { ...state.config, launchGift: event.detail } };
    if (logic.hasLaunchGift(state)) state = { ...state, inkCareSelected: false, longRulerSelected: false };
    render();
  });

  if (initializeState()) {
    initializeMediaGroups();
    render();
    initializeViewportVideos();
    installStickyVisibility();
  }
})();
